/**
 * resolvePhotoUrl — turns a photo reference into a directly-renderable URL.
 *
 * Handles four formats transparently:
 *   1. **Signed URL object** `{ url: "/api/files/...?sig=...&exp=..." }` (preferred)
 *      → returns `<BACKEND_URL>/api/files/...?sig=...&exp=...`  (no JWT)
 *   2. New backend path: "/api/files/qdrives/photos/.../abc.jpg"
 *      → returns "<BACKEND_URL>/api/files/...?auth=<jwt>"  (legacy fallback)
 *   3. Legacy data URL: "data:image/jpeg;base64,..." → returned as-is
 *   4. External absolute URL: "https://..." → returned as-is
 *
 * Always prefer signed-URL objects when the backend supplies them via
 * `vehicle.photos_signed[i]`. Migration target: stop using `?auth=` entirely.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function resolvePhotoUrl(ref) {
  if (!ref) return "";
  // Signed-URL object from backend (preferred)
  if (typeof ref === "object" && ref.url) {
    const u = ref.url;
    if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
    return u;
  }
  if (typeof ref !== "string") return "";
  if (ref.startsWith("data:") || ref.startsWith("http://") || ref.startsWith("https://")) {
    return ref;
  }
  if (ref.startsWith("/api/")) {
    // Already-signed URL: pass through (no JWT decoration needed)
    if (ref.includes("sig=") && ref.includes("exp=")) {
      return `${BACKEND_URL}${ref}`;
    }
    // Legacy: stitch the JWT
    const token = typeof window !== "undefined" ? localStorage.getItem("qdrives_token") : null;
    if (!token) return "";
    const sep = ref.includes("?") ? "&" : "?";
    return `${BACKEND_URL}${ref}${sep}auth=${encodeURIComponent(token)}`;
  }
  return ref;
}

/**
 * Convenience: pick the best photo source from a vehicle response.
 * Prefers `photos_signed[i]` (JWT-free) over the raw `photos[i]`.
 */
export function vehicleCoverPhoto(vehicle) {
  if (!vehicle) return "";
  if (vehicle.photos_signed && vehicle.photos_signed[0]) {
    return resolvePhotoUrl(vehicle.photos_signed[0]);
  }
  if (vehicle.photos && vehicle.photos[0]) {
    return resolvePhotoUrl(vehicle.photos[0]);
  }
  return "";
}
